// src/components/forms/shared/DisplayField.tsx
import React from 'react';
import { Box, Typography, Grid, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  FieldLabelContainer,
  FieldLabel,
  FieldInputContainer,
  FieldLabelContainerMultiline,
} from './FormElements';
import { colors } from '../../../theme/theme';

export interface DisplayFieldProps {
  label: string;
  value: string | React.ReactNode;
  multiline?: boolean;
  required?: boolean;
  className?: string;
  hideLabel?: boolean;
  icon?: React.ReactNode;
  backgroundColor?: string;
  hoverHighlight?: boolean;
  // Left side (label) customization
  labelBackgroundColor?: string;
  labelBackgroundColorMobile?: string;
  labelColor?: string;
  labelFontSize?: string;
  labelFontWeight?: string | number;
  labelFontSizeMobile?: string;
  labelPadding?: string;
  labelPaddingMobile?: string;
  // Right side (value) customization
  valueBackgroundColor?: string;
  color?: string;
  valueFontSize?: string;
  valueFontWeight?: string | number;
  valueFontSizeMobile?: string;
  valuePadding?: string;
  valuePaddingMobile?: string;
  flexDirection?: 'row' | 'column';
  hideColon?: boolean;
}

const isEmptyValue = (value: any): boolean => {
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase();
    return (
      lowerValue === 'not provided' ||
      lowerValue === 'no vet assigned' ||
      lowerValue === 'no notes' ||
      lowerValue === 'no name provided' ||
      lowerValue.includes('(other vet)')
    );
  }
  return false;
};

const CustomDisplayValue = styled(Typography, {
  shouldForwardProp: (prop) =>
    ![
      'customColor',
      'customValueFontSize',
      'customValueFontWeight',
      'isEmpty',
      'customValueFontSizeMobile',
    ].includes(prop as string),
})<{
  customColor?: string;
  customValueFontSize?: string;
  customValueFontWeight?: string | number;
  isEmpty?: boolean;
  customValueFontSizeMobile?: string;
}>(
  ({
    theme,
    customColor,
    customValueFontSize,
    customValueFontWeight,
    isEmpty,
    customValueFontSizeMobile,
  }) => ({
    fontSize: customValueFontSize || '2rem',
    fontWeight: customValueFontWeight || 500,
    wordBreak: 'break-word',
    minHeight: '2.4rem',
    display: 'flex',
    alignItems: 'center',
    color: isEmpty
      ? theme.palette.grey[400]
      : customColor || theme.palette.text.primary,
    fontStyle: isEmpty ? 'italic' : 'normal',
    whiteSpace: 'nowrap',
    paddingLeft: '0.5rem',
    overflowX: 'auto',
    overflowY: 'visible',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    paddingRight: '2rem',
    '&::-webkit-scrollbar': {
      display: 'none',
      paddingLeft: 0,
    },
    ...(customValueFontSizeMobile && {
      '@media (max-width: 899px)': {
        fontSize: customValueFontSizeMobile,
      },
    }),
    '@media (min-width: 600px)': {
      whiteSpcae: 'unset',
      paddingLeft: 0,
    },
  })
);

const CustomDisplayContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    ![
      'customColor',
      'customValueFontSize',
      'customValueFontWeight',
      'customValueFontSizeMobile',
    ].includes(prop as string),
})<{
  customColor?: string;
  customValueFontSize?: string;
  customValueFontWeight?: string | number;
  customValueFontSizeMobile?: string;
}>(
  ({
    theme,
    customColor,
    customValueFontSize,
    customValueFontWeight,
    customValueFontSizeMobile,
  }) => ({
    fontSize: customValueFontSize || '2rem',
    fontWeight: customValueFontWeight || 500,
    wordBreak: 'break-word',
    minHeight: '2.4rem',
    display: 'flex',
    alignItems: 'center',
    color: customColor || theme.palette.text.primary,
    ...(customValueFontSizeMobile && {
      '@media (max-width: 899px)': {
        fontSize: customValueFontSizeMobile,
      },
    }),
  })
);

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  margin: '0 0.5rem',
  color:
    theme.palette.mode === 'dark' ? colors.grey[0] : theme.palette.primary.main,
  '& .MuiSvgIcon-root': {
    fontSize: '2.4rem',
  },
}));

const CustomFieldLabel = styled(FieldLabel, {
  shouldForwardProp: (prop) =>
    ![
      'customLabelColor',
      'customLabelFontSize',
      'customLabelFontWeight',
      'customLabelFontSizeMobile',
    ].includes(prop as string),
})<{
  customLabelColor?: string;
  customLabelFontSize?: string;
  customLabelFontWeight?: string | number;
  customLabelFontSizeMobile?: string;
}>(
  ({
    customLabelColor,
    customLabelFontSize,
    customLabelFontWeight,
    customLabelFontSizeMobile,
  }) => ({
    ...(customLabelColor && { color: `${customLabelColor} !important` }),
    ...(customLabelFontSize && { fontSize: customLabelFontSize }),
    ...(customLabelFontWeight && { fontWeight: customLabelFontWeight }),
    ...(customLabelFontSizeMobile && {
      '@media (max-width: 899px)': {
        fontSize: customLabelFontSizeMobile,
      },
    }),
  })
);

const CustomFieldLabelContainer = styled(FieldLabelContainer, {
  shouldForwardProp: (prop) =>
    !['customBackgroundColor', 'customBackgroundColorMobile'].includes(
      prop as string
    ),
})<{
  customBackgroundColor?: string;
  customBackgroundColorMobile?: string;
}>(({ customBackgroundColor, customBackgroundColorMobile }) => ({
  ...(customBackgroundColor && { backgroundColor: customBackgroundColor }),
  borderRadius: '0 !important',
  transition: 'background-color 0.15s ease, color 0.15s ease',
  ...(customBackgroundColorMobile && {
    '@media (max-width: 899px)': {
      backgroundColor: customBackgroundColorMobile,
    },
  }),
}));

const CustomFieldLabelContainerMultiline = styled(
  FieldLabelContainerMultiline,
  {
    shouldForwardProp: (prop) =>
      !['customBackgroundColor', 'customBackgroundColorMobile'].includes(
        prop as string
      ),
  }
)<{
  customBackgroundColor?: string;
  customBackgroundColorMobile?: string;
}>(({ customBackgroundColor, customBackgroundColorMobile }) => ({
  ...(customBackgroundColor && { backgroundColor: customBackgroundColor }),
  borderRadius: '0 !important',
  ...(customBackgroundColorMobile && {
    '@media (max-width: 899px)': {
      backgroundColor: customBackgroundColorMobile,
    },
  }),
}));

const CustomFieldInputContainer = styled(FieldInputContainer, {
  shouldForwardProp: (prop) => prop !== 'customBackgroundColor',
})<{
  customBackgroundColor?: string;
}>(({ customBackgroundColor }) => ({
  ...(customBackgroundColor && { backgroundColor: customBackgroundColor }),
  borderRadius: '0 !important',
  '@media (max-width: 599.95px)': {
    width: '100% !important',
  },
}));

const isReactElement = (value: any): value is React.ReactElement => {
  return React.isValidElement(value);
};

const isSimpleValue = (value: any): value is string | number => {
  return typeof value === 'string' || typeof value === 'number';
};

export const DisplayField: React.FC<DisplayFieldProps> = ({
  label,
  value,
  multiline = false,
  required = false,
  className,
  hideLabel = false,
  icon,
  backgroundColor,
  hoverHighlight = false,
  labelBackgroundColor,
  labelBackgroundColorMobile,
  labelColor,
  labelFontSize,
  labelFontWeight,
  labelFontSizeMobile,
  labelPadding,
  labelPaddingMobile,
  valueBackgroundColor,
  color,
  valueFontSize,
  valueFontWeight,
  valueFontSizeMobile,
  valuePadding,
  valuePaddingMobile,
  flexDirection = 'row',
  hideColon = false,
}) => {
  const theme = useTheme();
  const isEmpty = isEmptyValue(value);

  const renderValue = () => {
    if (isReactElement(value)) {
      return (
        <CustomDisplayContainer
          customColor={color}
          customValueFontSize={valueFontSize}
          customValueFontWeight={valueFontWeight}
          customValueFontSizeMobile={valueFontSizeMobile}
        >
          {icon && hideLabel && <IconContainer>{icon}</IconContainer>}
          {value}
        </CustomDisplayContainer>
      );
    }

    if (isSimpleValue(value) || value == null) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {icon && hideLabel && <IconContainer>{icon}</IconContainer>}
          <CustomDisplayValue
            customColor={color}
            customValueFontSize={valueFontSize}
            customValueFontWeight={valueFontWeight}
            isEmpty={isEmpty}
            customValueFontSizeMobile={valueFontSizeMobile}
          >
            {value}
          </CustomDisplayValue>
        </Box>
      );
    }

    return (
      <CustomDisplayContainer
        customColor={color}
        customValueFontSize={valueFontSize}
        customValueFontWeight={valueFontWeight}
        customValueFontSizeMobile={valueFontSizeMobile}
      >
        {icon && hideLabel && <IconContainer>{icon}</IconContainer>}
        {value}
      </CustomDisplayContainer>
    );
  };

  const hoverBg =
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.dark
      : theme.palette.primary.main;
  // theme.palette.grey[800];

  const hoverSx = hoverHighlight
    ? {
        '&:hover .df-label-container': {
          backgroundColor: `${hoverBg} !important`,
          '& *': {
            color: '#ffffff !important',
          },
        },
      }
    : {};

  return (
    <Grid
      item
      xs={12}
      className={className}
      sx={{
        paddingRight: '0 !important',
        paddingLeft: '0 !important',
        '@media (min-width: 1536px) and (max-width: 2559px)': {
          paddingRight: '0 !important',
          paddingLeft: '0 !important',
        },
        '@media (min-width: 2560px)': {
          paddingLeft: '0 !important',
        },
        ...(backgroundColor
          ? {
              backgroundColor: `${backgroundColor} !important`,
              borderRadius: '0 !important',
            }
          : {
              borderRadius: '0 !important',
            }),
      }}
    >
      <Box
        display="flex"
        width="100%"
        sx={{
          borderRadius: '0 !important',
          flexDirection: flexDirection,
          ...(flexDirection === 'column' && {
            '& > *': {
              width: '100% !important',
            },
          }),
          '@media (min-width: 900px)': {
            flexDirection:
              flexDirection === 'row' ? 'row !important' : 'column !important',
          },
          '@media (max-width: 899px)': {
            flexDirection: 'column !important',
          },
          ...hoverSx,
        }}
      >
        {!hideLabel && (
          <>
            {multiline ? (
              <CustomFieldLabelContainerMultiline
                className="df-label-container"
                customBackgroundColor={labelBackgroundColor}
                customBackgroundColorMobile={labelBackgroundColorMobile}
                sx={{
                  ...(labelPadding && { padding: labelPadding }),
                  '@media (max-width: 899px)': {
                    ...(labelPaddingMobile && { padding: labelPaddingMobile }),
                  },
                }}
              >
                <CustomFieldLabel
                  customLabelColor={labelColor}
                  customLabelFontSize={labelFontSize}
                  customLabelFontWeight={labelFontWeight}
                  customLabelFontSizeMobile={labelFontSizeMobile}
                >
                  {label}
                  {required && <span className="required">*</span>}
                  {!hideColon && ' :'}
                </CustomFieldLabel>
              </CustomFieldLabelContainerMultiline>
            ) : (
              <CustomFieldLabelContainer
                className="df-label-container"
                customBackgroundColor={labelBackgroundColor}
                customBackgroundColorMobile={labelBackgroundColorMobile}
                sx={{
                  ...(labelPadding && { padding: labelPadding }),
                  '@media (max-width: 899px)': {
                    ...(labelPaddingMobile && { padding: labelPaddingMobile }),
                  },
                }}
              >
                <CustomFieldLabel
                  customLabelColor={labelColor}
                  customLabelFontSize={labelFontSize}
                  customLabelFontWeight={labelFontWeight}
                  customLabelFontSizeMobile={labelFontSizeMobile}
                >
                  {label}
                  {required && <span className="required">*</span>}
                  {!hideColon && ' :'}
                </CustomFieldLabel>
              </CustomFieldLabelContainer>
            )}
          </>
        )}
        <CustomFieldInputContainer
          className="displayFieldValue"
          customBackgroundColor={valueBackgroundColor}
          sx={{
            ...(hideLabel && { width: '100%' }),
            ...(valuePadding && { padding: valuePadding }),
            '@media (max-width: 899px)': {
              ...(valuePaddingMobile && { padding: valuePaddingMobile }),
            },
          }}
        >
          {renderValue()}
        </CustomFieldInputContainer>
      </Box>
    </Grid>
  );
};

export default DisplayField;
